import React, { useState, useEffect, useCallback } from 'react';
import StoreFootfallCharts from '../../../../src/component/StoreFootfallCharts';
import { format } from 'date-fns';
import { Grid, Card, Box, Stack, Typography, Button, useMediaQuery, CircularProgress } from '@mui/material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import settings from 'configs/react-slick-config';
import ImageViewerModal from 'components/ImageViewerModal';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Marquee from 'react-fast-marquee';
import MapComponent from '../../../../src/views/Stores/Insight/storePage';
import { EvidenceImagesSkeleton, FootfallDataSkeleton, MapSkeleton } from 'component/SkeletonLoader';
import { getUniqueWalkinWalkout, STORES_API } from 'api';

const INITIAL_IMAGES_COUNT = 5;
const LOAD_MORE_IMAGES_COUNT = 5;

const LazyImage = ({ src, alt, style, className, onError, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [imgRef, setImgRef] = useState(null);

  useEffect(() => {
    if (!imgRef) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(imgRef);
    return () => observer.disconnect();
  }, [imgRef]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  return (
    <div ref={setImgRef} style={style} className={className} onClick={onClick}>
      {!isInView ? (
        <div
          style={{
            ...style,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f0f0f0'
          }}
        >
          <CircularProgress size={24} />
        </div>
      ) : (
        <>
          {!isLoaded && (
            <div
              style={{
                ...style,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f0f0f0',
                zIndex: 1
              }}
            >
              <CircularProgress size={24} />
            </div>
          )}
          <img
            src={src}
            alt={alt}
            style={{
              ...style,
              opacity: isLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease'
            }}
            className={className}
            onLoad={handleLoad}
            onError={onError}
            loading="lazy"
          />
        </>
      )}
    </div>
  );
};

const StatusButton = ({ item }) => {
  const status = item.status || 'offline';
  const getStatusColor = () => {
    switch (status.toLowerCase()) {
      case 'online':
        return 'bg-green-500';
      case 'offline':
        return 'bg-red-500';
      case 'maintenance':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  return <div className={`w-4 h-4 rounded-full ${getStatusColor()}`} />;
};

const EvidenceImages = ({ recent, onImageClick, formatTimestamp }) => {
  const [visibleImages, setVisibleImages] = useState(INITIAL_IMAGES_COUNT);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const loadMoreImages = useCallback(() => {
    if (isLoadingMore || visibleImages >= recent.length) return;

    setIsLoadingMore(true);
    setTimeout(() => {
      setVisibleImages((prev) => Math.min(prev + LOAD_MORE_IMAGES_COUNT, recent.length));
      setIsLoadingMore(false);
    }, 300);
  }, [recent.length, visibleImages, isLoadingMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const lastEntry = entries[0];
        if (lastEntry.isIntersecting && visibleImages < recent.length && !isLoadingMore) {
          loadMoreImages();
        }
      },
      {
        threshold: 0.5,
        rootMargin: '50px'
      }
    );

    const lastImageElement = document.querySelector(`[data-image-index="${visibleImages - 1}"]`);
    if (lastImageElement && visibleImages < recent.length) {
      observer.observe(lastImageElement);
    }

    return () => observer.disconnect();
  }, [visibleImages, recent.length, isLoadingMore, loadMoreImages]);

  const customSettings = {
    ...settings,
    slidesToShow: Math.min(visibleImages, 5),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: Math.min(visibleImages, 5)
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 5
        }
      }
    ]
  };

  const handleImageError = (e, evidence) => {
    console.error('Failed to load image:', evidence.thumbnail);
    e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Available';
  };

  const visibleEvidences = recent.slice(0, visibleImages);
  // const hasMoreImages = visibleImages < recent.length;

  return (
    <>
      <Typography
        variant="h6"
        sx={{
          mb: 1,
          width: '100%',
          textAlign: 'center',
          fontWeight: 600,
          color: '#10B981',
          padding: '4px 0',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        Evidences ({recent.length})
      </Typography>
      <div style={{ width: '100%', overflow: 'visible', position: 'relative' }}>
        <Slider {...customSettings} className="evidence-slider">
          {visibleEvidences.map((evidence, idx) => (
            <div key={idx} data-image-index={idx} style={{ padding: '0 8px' }}>
              <div
                onClick={() => onImageClick(recent, idx)}
                className="rounded-md border shadow-md relative hover:shadow-lg transition-shadow duration-300"
                style={{ height: '180px', overflow: 'hidden', cursor: 'pointer' }}
              >
                <LazyImage
                  style={{
                    width: '100%',
                    height: '180px',
                    objectFit: 'cover',
                    backgroundColor: '#f0f0f0',
                    display: 'block',
                    transition: 'transform 0.3s ease'
                  }}
                  className="hover:scale-105"
                  src={evidence.thumbnail || 'https://via.placeholder.com/300x200?text=No+Image'}
                  alt={`Evidence ${idx + 1}`}
                  onError={(e) => handleImageError(e, evidence)}
                  onClick={() => onImageClick(recent, idx)}
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-2 text-center">
                  {formatTimestamp(evidence.time_stamp)}
                </div>
              </div>
            </div>
          ))}
        </Slider>

        {isLoadingMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="caption" sx={{ ml: 1, alignSelf: 'center' }}>
              Loading more images...
            </Typography>
          </Box>
        )}
      </div>
    </>
  );
};

// Main Dashboard Component
const CameraPage1 = () => {
  const navigate = useNavigate();
  const [storesData, setStoresData] = useState(null);
  const [loading, setLoading] = useState(true);
  // const [setFetchFlag] = useState(false);
  const [footfallData, setFootfallData] = useState({});
  const [isFetchingFootfall, setIsFetchingFootfall] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedStoreImages, setSelectedStoreImages] = useState([]);
  const [mapLoading, setMapLoading] = useState(true);

  // Get the current date from Redux store
  const currentDate = useSelector((state) => state.customization.currentDate);
  const selectedTime = useSelector((state) => state.customization.selectedTime);

  // Media queries for responsive design
  const isSm = useMediaQuery('(max-width:600px)');
  const isLg = useMediaQuery('(min-width:1200px)');

  // Handle image click to view full size
  const handleImageClick = (images, imageIndex) => {
    console.log('Image clicked, images:', images, 'index:', imageIndex);

    if (!images || !Array.isArray(images) || images.length === 0) {
      console.error('No images available');
      return;
    }

    setSelectedStoreImages(images);
    setSelectedImageIndex(imageIndex);
    setModalOpen(true);
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    try {
      return format(new Date(timestamp), 'hh:mm a');
    } catch (error) {
      return 'Invalid time';
    }
  };



  // Transform stores data from API
  const transformStoresData = (rawData) => {
    try {
      if (!Array.isArray(rawData)) {
        console.error('transformStoresData received non-array data:', rawData);
        return [];
      }

      return rawData
        .map((store) => {
          try {
            const location = store.location || {};
            const lat = location.latitude ? location.latitude.toString() : '0';
            const lng = location.longitude ? location.longitude.toString() : '0';

            console.log('Store:', store.name, 'lat & lng:', lat, lng);

            return {
              storeDetails: [
                {
                  storeID: store.id || store._id,
                  store_id: store.store_id || 'N/A',
                  name: store.name || 'Unnamed Store',
                  status: 'online',
                  locationData: {
                    lat: lat,
                    lng: lng
                  },
                  source: store.source
                }
              ],
              recent: store.recent || [],
              anomalies_details: [],
              anomalies_solved: 0
            };
          } catch (error) {
            console.error('Error transforming store data:', error, 'Store:', store);
            return null;
          }
        })
        .filter(Boolean);
    } catch (error) {
      console.error('Error in transformStoresData:', error);
      return [];
    }
  };

  // Fetch footfall data for a store
  const fetchFootfallData = async (source, selectedDate) => {
    try {
      const formattedDate = selectedDate ? new Date(selectedDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

      const requestData = {
        date: formattedDate,
        source_id: source,
        start_time: new Date(selectedTime?.start_time),
        end_time: new Date(selectedTime?.end_time)
      };

      console.log('Fetching footfall data with:', requestData);

      const response = await getUniqueWalkinWalkout(requestData);
      const data = response.data.data[0]?.footfall;

      if (!data || data.zoneFootfall.length === 0) {
        return { storeFootfall: 0, employeeFootfall: 0, zoneFootfall: [] };
      } else if (data && data.zoneFootfall.length > 0) {
        return {
          storeFootfall: data.store_footfall,
          employeeFootfall: data.employee_footfall,
          zoneFootfall: data.zoneFootfall
        };
      }
    } catch (error) {
      console.log('Error fetching footfall data:', error);
      return { storeFootfall: 0, employeeFootfall: 0, zoneFootfall: [] };
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    const user_id = localStorage.getItem('user_id');
    console.log('User ID from localStorage:', user_id);

    const fetchData = async () => {
      try {
        setLoading(true);
        setMapLoading(true);
        console.log('Fetching stores data from:', STORES_API);

        const storesResponse = await fetch(STORES_API, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_id,
            date: currentDate.split('T')[0],
            start_time: new Date(selectedTime?.start_time),
            end_time: new Date(selectedTime?.end_time)
          })
        });

        if (!storesResponse.ok) {
          throw new Error(`API request failed with status ${storesResponse.status}`);
        }

        const storesJson = await storesResponse.json();
        console.log('Stores API response:', storesJson);
        console.log('dsdd', storesJson.stores);

        if (!storesJson || !storesJson.stores || !Array.isArray(storesJson.stores)) {
          console.error('Invalid stores data format:', storesJson);
          setLoading(false);
          setMapLoading(false);
          return;
        }

        console.log('Number of stores received:', storesJson.stores.length);
        const transformedStores = transformStoresData(storesJson.stores);
        console.log('Transformed stores data:', transformedStores);

        setStoresData(transformedStores);

        // Fetch footfall data for each store
        const footfallPromises = transformedStores.map(async (store) => {
          const sourceId = store.storeDetails[0].source;
          console.log('Fetching footfall data for store:', store.storeDetails[0].name, 'with source ID:', sourceId);
          const data = await fetchFootfallData(sourceId, currentDate);
          return { sourceId, data };
        });

        const footfallResults = await Promise.all(footfallPromises);
        const footfallMap = {};

        footfallResults.forEach((result) => {
          footfallMap[result.sourceId] = result.data;
        });

        console.log('Footfall data map:', footfallMap);
        setFootfallData(footfallMap);
        setLoading(false);
        setMapLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
        setMapLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, selectedTime?.start_time, selectedTime?.end_time]);

  // Effect to refetch footfall data ONLY when date changes
  useEffect(() => {
    if (storesData && !loading && !isFetchingFootfall) {
      const refetchFootfallData = async () => {
        try {
          setIsFetchingFootfall(true);
          console.log('Date changed, refetching footfall data for:', currentDate);

          const footfallPromises = storesData.map(async (store) => {
            const sourceId = store.storeDetails[0].source;
            console.log('sss', store.storeDetails[0]);
            const data = await fetchFootfallData(sourceId, currentDate);
            return { sourceId, data };
          });

          const footfallResults = await Promise.all(footfallPromises);
          const footfallMap = {};

          footfallResults.forEach((result) => {
            footfallMap[result.sourceId] = result.data;
          });

          setFootfallData(footfallMap);
        } catch (error) {
          console.error('Error refetching footfall data:', error);
        } finally {
          setIsFetchingFootfall(false);
        }
      };

      refetchFootfallData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, selectedTime]);

  return (
    <Grid container spacing={2}>
      <Grid key={index} xs={12} item>
        <Card className="shadow-xl store" sx={{ padding: 2 }}>
          <Grid container spacing={1}>
            <Grid item lg={7} md={6} sm={9} xs={12}>
              <Grid xs={12} item container spacing={0}>
                {!isSm && (
                  <Grid className="w-[150px]" sx={{ paddingRight: 1 }}>
                    {mapLoading ? (
                      <MapSkeleton />
                    ) : (
                      <MapComponent
                        lat={item.storeDetails[0].locationData.lat}
                        lng={item.storeDetails[0].locationData.lng}
                        onLoad={() => setMapLoading(false)}
                        onError={() => setMapLoading(false)}
                      />
                    )}
                  </Grid>
                )}
                <Grid sx={{ display: 'flex', flex: 1 }}>
                  <Stack sx={{ width: '100%', height: 'fit-content' }} direction={'column'} spacing={1}>
                    <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                      <Stack direction={'row'} spacing={1} sx={{ flex: 1 }}>
                        <div className="h-full flex pt-1 flex-col justify-start storeStatus">
                          <StatusButton item={item.storeDetails[0]} />
                        </div>
                        <Box
                          className="drop-shadow-md self-start cursor-pointer storeName"
                          sx={{ width: '100%' }}
                          onClick={() => {
                            navigate('/main/stores/storeinsight');
                            localStorage.setItem('source', JSON.stringify(item.storeDetails[0].source));
                            localStorage.setItem('storeName', `${item.storeDetails[0].store_id} - ${item.storeDetails[0].name}`);
                            localStorage.setItem('store_id', JSON.stringify(item.storeDetails[0].storeID));
                          }}
                        >
                          {!isLg ? (
                            <Marquee>
                              <Typography variant="h5">
                                {item.storeDetails[0].store_id} - {item.storeDetails[0].name}
                              </Typography>
                            </Marquee>
                          ) : (
                            <Typography variant="h5">
                              {item.storeDetails[0].store_id} - {item.storeDetails[0].name}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                      <Button
                        onClick={() => {
                          navigate(`/main/stores/storeinsight`);
                          localStorage.setItem('source', JSON.stringify(item.storeDetails[0].source));
                          localStorage.setItem('storeName', `${item.storeDetails[0].store_id} - ${item.storeDetails[0].name}`);
                          localStorage.setItem('store_id', JSON.stringify(item.storeDetails[0].storeID));
                        }}
                        variant="outlined"
                      >
                        Analysis
                      </Button>
                    </Box>

                    {/* Chart-based Visualization for Footfall Data */}
                    <Box sx={{ width: '100%' }}>
                      <Grid item lg={12} md={12} sm={12} xs={12} className="kpiScore">
                        {isFetchingFootfall || !footfallData[item.storeDetails[0].source] ? (
                          <FootfallDataSkeleton />
                        ) : (
                          <StoreFootfallCharts footfallData={footfallData} storeSource={item.storeDetails[0].source} />
                        )}
                      </Grid>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>

            <Grid item lg={5} md={4} sm={12} xs={12} className="anomalyImages">
              <div className="w-full px-4 flex flex-col h-full">
                {loading ? (
                  <EvidenceImagesSkeleton />
                ) : item.recent && item.recent.length > 0 ? (
                  <EvidenceImages recent={item.recent} onImageClick={handleImageClick} formatTimestamp={formatTimestamp} />
                ) : (
                  <div className="w-full h-full flex justify-center place-items-center">
                    <Stack direction={'column'} spacing={1}>
                      <div className="w-28 flex justify-center"></div>
                      <div className="w-28 h-8 text-base flex justify-center">No Evidences</div>
                    </Stack>
                  </div>
                )}
              </div>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      {/* Image Viewer Modal */}
      <ImageViewerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        images={selectedStoreImages}
        initialIndex={selectedImageIndex}
        formatTimestamp={formatTimestamp}
      />
    </Grid>
  );
};

export default CameraPage1;
